<template>
    <div class="data-container">
        <div class="tool-box">
            <el-button type="primary" :icon="Refresh" @click="resetQueryForm">刷新</el-button>
            <el-button :icon="Upload" @click="handleExport" v-permission="['heating:history-event:export']">导出</el-button>
        </div>
        <el-form class="search-form" :model="query_params" label-position="top" inline>
            <el-form-item label="日期">
                <el-date-picker
                    v-model="query_params.date_range"
                    style="width: 250px"
                    type="datetimerange"
                    value-format="YYYY-MM-DD HH:mm:ss"
                    start-placeholder="开始日期"
                    end-placeholder="结束日期"
                />
            </el-form-item>
            <el-form-item label="事件类型">
                <el-select v-model="query_params.alarm_type" placeholder="请选择状态" style="width: 200px" clearable>
                    <el-option v-for="item in alarm_type_list" :key="item.code" :label="item.name" :value="item.code"></el-option>
                </el-select>
            </el-form-item>
            <el-form-item>
                <el-button type="primary" :icon="Search" plain @click="handleSearch">搜索</el-button>
                <el-button :icon="Refresh" @click="resetQueryForm">重置</el-button>
            </el-form-item>
        </el-form>
        <el-table class="data-table" :data="table_data" v-loading="tableLoading" table-layout="auto">
            <el-table-column label="编号" width="80" type="index" />
            <!--                            发生时间、事件编码、事件类型、事件源（主机设备）、告警级别（告警有级别、事件无级别）、处理状态（确认/待确认/误报/忽略）、事件发生区域-->
            <el-table-column prop="happen_time" label="发生时间" />
            <!--                            <el-table-column prop="event_code" label="事件编码" />-->
            <el-table-column prop="alarm_type" label="事件类型">
                <template #default="{ row }">
                    <el-tag v-if="row.alarm_type === 1" type="warning">掉电故障</el-tag>
                    <el-tag v-if="row.alarm_type === 2" type="warning">位移故障</el-tag>
                    <el-tag v-if="row.alarm_type === 3" type="warning">离线故障</el-tag>
                    <el-tag v-if="row.alarm_type === 4" type="warning">市电掉电报警</el-tag>
                    <el-tag v-if="row.alarm_type === 5" type="warning">电池低电压报警</el-tag>
                    <el-tag v-if="row.alarm_type === 6" type="warning">防拆报警</el-tag>
                    <el-tag v-if="row.alarm_type === 7" type="warning">阀门堵转</el-tag>
                    <el-tag v-if="row.alarm_type === 8" type="warning">温度传感器报警</el-tag>
                </template>
            </el-table-column>
            <el-table-column prop="device_name" label="设备名称" />
            <el-table-column prop="device_imei" label="IMEI" />
            <!--                            <el-table-column prop="level" label="告警级别" width="100" />-->
            <el-table-column label="事件发生区域" prop="location">
                <template #default="{ row }">
                    <span>{{ room_list[row.room] }}</span>
                </template>
            </el-table-column>
            <el-table-column v-if="route.query.type == 3" label="操作" width="120">
                <template #default="{ row }">
                    <el-button type="primary" link :icon="Delete" @click="handleDeleteAlarmLog(row.id)">删除报警记录</el-button>
                </template>
            </el-table-column>
        </el-table>
        <el-pagination
            v-if="table_data.length > 0"
            class="pagination"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="query_params.page"
            :page-sizes="[10, 20, 50]"
            :page-size="query_params.page_size"
            layout="total, sizes, prev, pager, next, jumper"
            :total="total"
        ></el-pagination>
    </div>
</template>

<script setup>
import { onMounted, ref, watch } from 'vue'
import { Delete, Refresh, Search, Upload } from '@element-plus/icons'
import { ElMessage, ElMessageBox } from 'element-plus'
import { getAlarmLogList, deleteAlarmLog } from '@/api/heating/monitoring.js'
import { getOptionTree } from '@/api/room.js'
import { useRoute } from 'vue-router'

const route = useRoute()

// 重置
const resetQueryForm = () => {
    query_params.value = {
        date_range: null,
        alarm_type: '',
        page: 1,
        page_size: 10,
        type: route.query.type
    }
    getTableData()
}

// 数据源
const query_params = ref({
    date_range: null,
    alarm_type: '',
    page: 1,
    page_size: 10,
    type: route.query.type
})
const table_data = ref([])
const tableLoading = ref(false)
const total = ref(0)

// 状态列表
const alarm_type_list = ref([
    { code: '1', name: '掉电故障' },
    { code: '2', name: '位移故障' },
    { code: '3', name: '离线故障' },
    { code: '4', name: '市电掉电报警' },
    { code: '5', name: '电池低电压报警' },
    { code: '6', name: '防拆报警' },
    { code: '7', name: '阀门堵转' },
    { code: '8', name: '温度传感器报警' }
])

// 安装位置
const room_list = ref({})
async function getRoomTree() {
    const room_locations = {}
    function getRoom(list, location) {
        list.forEach((item) => {
            if (item.children) {
                getRoom(item.children, location + '/' + item.name)
            } else {
                room_locations[item.id] = location.slice(1) + '/' + item.name
            }
        })
    }
    await getOptionTree().then((data) => {
        getRoom(data[0]?.children ?? [], '')
        room_list.value = room_locations
    })
}

// 获取列表数据
const getTableData = async () => {
    tableLoading.value = true
    const params = { ...query_params.value }
    if (params.date_range && params.date_range.length) {
        params.start_time = params.date_range[0]
        params.end_time = params.date_range[1]
    }
    delete params.date_range
    params.type = route.query.type
    const result = await getAlarmLogList(params)
    result.lists.forEach((item) => {
        switch (item.level) {
            case 1:
                item.color = '#FF4346'
                break
            case 2:
                item.color = '#FF7E00'
                break
            case 3:
                item.color = '#FBCA07'
                break

            default:
                item.color = '#606266'
                break
        }
    })
    table_data.value = result.lists
    total.value = result.total
    tableLoading.value = false
}

// 分页相关
// page_size 改变触发
const handleSizeChange = (currentSize) => {
    query_params.value.page_size = currentSize
    query_params.value.page = 1
    getTableData()
}
// page 页码改变触发
const handleCurrentChange = (currentPage) => {
    query_params.value.page = currentPage
    getTableData()
}

const formDialogVisible = ref(false)
const selectModelId = ref('') // 点中的模块id
// 保证每次打开重新获取数据
watch(formDialogVisible, (val) => {
    if (!val) selectModelId.value = ''
})

// 搜索、刷新
function handleSearch() {
    query_params.page = 1
    getTableData()
}

// 导出
const handleExport = async () => {
    ElMessageBox.confirm('您确定导出历史告警事件列表吗？', {
        type: 'warning'
    })
        .then(async () => {
            const params = {
                ...query_params.value,
                export: 1
            }
            if (params.date_range && params.date_range.length) {
                params.start_time = params.date_range[0]
                params.end_time = params.date_range[1]
            }
            delete params.date_range
            const { data } = await getAlarmLogList(params)
            window.open(data)

            ElMessage.success('导出成功')
        })
        .catch(() => {})
}
const handleDeleteAlarmLog = (id) => {
    ElMessageBox.confirm('您确定删除报警记录吗？', {
        type: 'warning'
    })
        .then(async () => {
            await deleteAlarmLog(id)
            ElMessage.success('删除成功')
            getTableData()
        })
        .catch(() => {})
}

onMounted(() => {
    getRoomTree()
    getTableData()
})
</script>

<style scoped></style>
